import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { KeyboardArrowUp as ArrowUpIcon, KeyboardArrowDown as ArrowDownIcon } from '@mui/icons-material';
import { Unit, Lease } from '@urbanmix-tech/shared-js';
import dayjs from 'dayjs';
import { getLongNumberStr } from '@/helpers/common';
import { formatLeaseLengthValue } from '@/helpers/leaseHelpers';
import { GroupSummary, calculateLeaseMetrics, concessionsToMonths } from './comparablesUtils';
import { ColorGradeVisualizer } from '@urbanmix-tech/um-embedded';

export interface ProposedMetrics {
  monthlyRent: number;
  leaseLength: number;
  concessions: number;
  effectiveMonthlyRent: number;
  annualRentPerSqft: number;
  effectiveAnnualRentPerSqft: number;
}

interface ComparableUnitTableProps {
  units: Unit[];
  leaseMap: Map<string, Lease>;
  summary: GroupSummary | null;
  proposedMetrics?: ProposedMetrics;
  selectedUnitSqft?: number;
  visualizer?: ColorGradeVisualizer;
  selectedUnit?: Unit;
  showComparabilityReason?: boolean;
  hoveredUnitId?: string | null;
  onHoverUnit?: (unitId: string | null) => void;
  visibleColumns?: Set<string>;
  onColumnsChange?: (columns: Set<string>) => void;
  showConcessionsAsMultiples?: boolean;
}

export function ComparableUnitTable({
  units,
  leaseMap,
  summary,
  proposedMetrics,
  selectedUnitSqft,
  visualizer,
  selectedUnit,
  showComparabilityReason,
  hoveredUnitId,
  onHoverUnit,
  visibleColumns: propVisibleColumns,
  onColumnsChange,
  showConcessionsAsMultiples = true,
}: ComparableUnitTableProps) {
  // Sorting state
  type SortField = 'unitName' | 'line' | 'bedrooms' | 'sqft' | 'rent' | 'ppsf' | 'effectiveRent' | 'effectivePPSF' | 'concessions' | 'leaseLength' | 'leaseStart' | 'leaseEnd' | 'comparabilityReason';
  const [sortField, setSortField] = useState<SortField>('effectivePPSF');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Column visibility state - hide BR and Sqft by default
  const defaultVisibleColumns = new Set(['unitName', 'line', 'bedrooms', 'sqft', 'rent', 'ppsf', 'effectiveRent', 'effectivePPSF', 'concessions', 'leaseLength', 'leaseStart', 'leaseEnd']);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(propVisibleColumns || defaultVisibleColumns);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleColumnVisibility = (columnKey: string) => {
    const newVisibleColumns = new Set(visibleColumns);
    if (newVisibleColumns.has(columnKey)) {
      newVisibleColumns.delete(columnKey);
    } else {
      newVisibleColumns.add(columnKey);
    }
    setVisibleColumns(newVisibleColumns);
    onColumnsChange?.(newVisibleColumns);
  };

  // Helper function to determine why a unit is comparable
  const getComparabilityReason = (unit: Unit): string[] => {
    if (!selectedUnit) return [];
    const reasons: string[] = [];

    // Check if same line
    const selectedLine = selectedUnit.type || selectedUnit.line;
    const unitLine = unit.type || unit.line;
    if (unitLine === selectedLine) {

      reasons.push('Line');
    }

    // Check if same bedrooms
    if (unit.numBedrooms === selectedUnit.numBedrooms) {
      reasons.push('BR');
    }

    // Check if similar sqft (±10%)
    const sqftRange = selectedUnit.sqft * 0.1;
    const minSqft = selectedUnit.sqft - sqftRange;
    const maxSqft = selectedUnit.sqft + sqftRange;
    if (unit.sqft >= minSqft && unit.sqft <= maxSqft) {
      reasons.push('Sqft');
    }

    return reasons;
  };

  let rows = units
    .map((unit) => {
      const lease = leaseMap.get(unit.id);
      if (!lease) return null;

      // Use shared utility for consistent calculations
      const metrics = calculateLeaseMetrics(unit, lease);
      if (!metrics) return null;

      return {
        unitId: unit.id,
        unitName: unit.name || unit.id,
        line: unit.type || unit.line || '-',
        bedrooms: unit.numBedrooms,
        sqft: metrics.sqft,
        rent: Math.round(metrics.monthlyRent * 100) / 100,
        monthlyRent: metrics.monthlyRent,
        ppsf: Math.round(metrics.annualRentPerSqft * 100) / 100,
        effectiveRent: metrics.effectiveMonthlyRent,
        effectivePPSF: metrics.effectiveAnnualRentPerSqft,
        concessions: metrics.concessions,
        leaseLength: metrics.leaseLength,
        leaseStart: metrics.leaseStartDate,
        leaseEnd: metrics.leaseEndDate,
        comparabilityReason: showComparabilityReason ? getComparabilityReason(unit) : [],
        isProposed: false,
      };
    })
    .filter(Boolean) as any[];

  // Add proposed metrics as a row if available
  if (proposedMetrics && selectedUnitSqft) {
    rows.push({
      unitId: '__proposed__',
      unitName: selectedUnit?.name || "New Terms" ,
      line: selectedUnit?.type || selectedUnit?.line || '-',
      bedrooms: selectedUnit?.numBedrooms || "Studio",
      sqft: selectedUnitSqft,
      rent: Math.round(proposedMetrics.monthlyRent * 100) / 100,
      monthlyRent: proposedMetrics.monthlyRent,
      ppsf: Math.round(proposedMetrics.annualRentPerSqft * 100) / 100,
      effectiveRent: Math.round(proposedMetrics.effectiveMonthlyRent * 100) / 100,
      effectivePPSF: Math.round(proposedMetrics.effectiveAnnualRentPerSqft * 100) / 100,
      concessions: Math.round(proposedMetrics.concessions * 100) / 100,
      leaseLength: proposedMetrics.leaseLength,
      leaseStart: null,
      leaseEnd: null,
      comparabilityReason: [],
      isProposed: true,
    });
  }

  // Sort rows
  rows = rows.sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];

    if (typeof aValue === 'string') {
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortDirection === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });

  const SortableHeader = ({ field, label, align = 'left', width }: { field: SortField; label: string; align?: 'left' | 'right'; width?: string }) => (
    <TableCell
      align={align as any}
      className="text-zinc-300 py-1 px-2 !text-xs cursor-pointer hover:bg-zinc-700/30 dark:hover:bg-zinc-700/30 transition-colors font-normal"
      onClick={() => handleSort(field)}
      style={width ? { width, ...headerCellStyle } : headerCellStyle}
      sx={headerCellSx}
    >
      <div className="flex items-center gap-0.5" style={{ justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        <span>{label}</span>
        {sortField === field && (
          sortDirection === 'asc' ? (
            <ArrowUpIcon fontSize="small" />
          ) : (
            <ArrowDownIcon fontSize="small" />
          )
        )}
      </div>
    </TableCell>
  );

  const headerCellStyle = { fontSize: '0.7rem !important' };
  const headerCellSx = { wordBreak: 'break-word', whiteSpace: 'normal' };

  if (rows.length === 0) {
    return (
      <div className="text-center py-4">
        <Typography variant="body2" color="textSecondary">
          No comparable units found
        </Typography>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 overflow-x-auto">
      <Table size="small" className="border-collapse" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow className="border-b border-zinc-300 dark:border-zinc-600">
            <TableCell className="py-1 px-2" style={{ width: '20px' }} />
            {visibleColumns.has('unitName') && <SortableHeader field="unitName" label="Unit" width="40px" />}
            {showComparabilityReason && (
              <SortableHeader field="comparabilityReason" label="Comp. By" width="60px" />
            )}
            {visibleColumns.has('line') && <SortableHeader field="line" label="Line" width="40px" />}
            {visibleColumns.has('bedrooms') && <SortableHeader field="bedrooms" label="BR" align="right" width="35px" />}
            {visibleColumns.has('sqft') && <SortableHeader field="sqft" label="Sqft" align="right" width="55px" />}
            {visibleColumns.has('rent') && <SortableHeader field="rent" label="Rent" align="right" width="50px" />}
            {visibleColumns.has('ppsf') && <SortableHeader field="ppsf" label="Gross PPSF" align="right" width="60px" />}
            {visibleColumns.has('concessions') && <SortableHeader field="concessions" label={showConcessionsAsMultiples ? "Conc. (mo)" : "Conc. ($)"} align="right" width="60px" />}
            {visibleColumns.has('effectiveRent') && <SortableHeader field="effectiveRent" label="Net Rent" align="right" width="60px" />}
            {visibleColumns.has('effectivePPSF') && <SortableHeader field="effectivePPSF" label="Net PPSF" align="right" width="60px" />}
            {visibleColumns.has('leaseLength') && <SortableHeader field="leaseLength" label="Length" align="right" width="40px" />}
            {visibleColumns.has('leaseStart') && <SortableHeader field="leaseStart" label="Lease Start" align="right" width="70px" />}
            {visibleColumns.has('leaseEnd') && <SortableHeader field="leaseEnd" label="Lease End" align="right" width="70px" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            let color = '#999999';
            try {
              if (visualizer && visualizer.getColor) {
                color = visualizer.getColor(row.effectivePPSF);
              }
            } catch (e) {
              console.error('Error getting color:', e);
            }

            const isProposedRow = row.isProposed;
            const isHovered = hoveredUnitId === row.unitId;

            return (
              <TableRow
                key={row.unitId}
                onMouseEnter={() => onHoverUnit?.(row.unitId)}
                onMouseLeave={() => onHoverUnit?.(null)}
                sx={{
                  backgroundColor: isHovered ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                  transition: 'background-color 0.2s ease',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  },
                }}
              >
                <TableCell className="py-2 px-2" style={{ width: '40px' }}>
                  <div
                    className="w-6 h-6"
                    style={{ backgroundColor: color }}
                  />
                </TableCell>
                {visibleColumns.has('unitName') && (
                  <TableCell className={`py-2 px-2 text-xs ${isProposedRow ? 'font-bold text-blue-100' : 'text-zinc-300'}`}>
                    {row.unitName}
                  </TableCell>
                )}
                {showComparabilityReason && (
                  <TableCell className={`py-2 px-2 text-xs ${isProposedRow ? 'text-blue-100' : 'text-zinc-300'}`}>
                    {row.comparabilityReason.length > 0 ? row.comparabilityReason.join(', ') : '-'}
                  </TableCell>
                )}
                {visibleColumns.has('line') && (
                  <TableCell className={`py-2 px-2 text-xs ${isProposedRow ? 'font-bold text-blue-100' : 'text-zinc-300'}`}>
                    {row.line}
                  </TableCell>
                )}
                {visibleColumns.has('bedrooms') && (
                  <TableCell align="right" className={`py-2 px-2 ${isProposedRow ? 'font-bold text-blue-100' : 'text-zinc-300'}`}>
                    {row.bedrooms ?? '-'}
                  </TableCell>
                )}
                {visibleColumns.has('sqft') && (
                  <TableCell align="right" className={`py-2 px-2 ${isProposedRow ? 'font-bold text-blue-100' : 'text-zinc-300'}`}>
                    {Math.round(row.sqft)}
                  </TableCell>
                )}
                {visibleColumns.has('rent') && (
                  <TableCell align="right" className={`py-2 px-2 ${isProposedRow ? 'font-bold text-blue-100' : 'text-zinc-300'}`}>
                    ${getLongNumberStr(Math.round(row.rent))}
                  </TableCell>
                )}
                {visibleColumns.has('ppsf') && (
                  <TableCell align="right" className={`py-2 px-2 ${isProposedRow ? 'font-bold text-blue-100' : 'text-zinc-300'}`}>
                    ${row.ppsf.toFixed(2)}
                  </TableCell>
                )}
                 {visibleColumns.has('concessions') && (
                  <TableCell align="right" className={`py-2 px-2 ${isProposedRow ? 'font-bold text-blue-100' : 'text-zinc-300'}`}>
                    {showConcessionsAsMultiples
                      ? `${concessionsToMonths(row.concessions, row.monthlyRent)} mo`
                      : `$${getLongNumberStr(Math.round(row.concessions))}`}
                  </TableCell>
                )}
                {visibleColumns.has('effectiveRent') && ( 
                  <TableCell align="right" className={`py-2 px-2 ${isProposedRow ? 'font-bold text-blue-100' : 'text-zinc-300'}`}>
                    ${getLongNumberStr(Math.round(row.effectiveRent))}
                  </TableCell>
                )}
                {visibleColumns.has('effectivePPSF') && (
                  <TableCell align="right" className={`py-2 px-2 ${isProposedRow ? 'font-bold text-blue-100' : 'text-zinc-300'}`}>
                    ${row.effectivePPSF.toFixed(2)}
                  </TableCell>
                )}
                {visibleColumns.has('leaseLength') && (
                  <TableCell align="right" className={`py-2 px-2 ${isProposedRow ? 'font-bold text-blue-100' : 'text-zinc-300'}`}>
                    {formatLeaseLengthValue(row.leaseLength)}
                  </TableCell>
                )}
                {visibleColumns.has('leaseStart') && (
                  <TableCell align="right" className={`py-2 px-2 ${isProposedRow ? 'font-bold text-blue-100' : 'text-zinc-300'}`}>
                    {row.leaseStart ? dayjs(row.leaseStart).format('MM/DD/YYYY') : '-'}
                  </TableCell>
                )}
                {visibleColumns.has('leaseEnd') && (
                  <TableCell align="right" className={`py-2 px-2 ${isProposedRow ? 'font-bold text-blue-100' : 'text-zinc-300'}`}>
                    {row.leaseEnd ? dayjs(row.leaseEnd).format('MM/DD/YYYY') : '-'}
                  </TableCell>
                )}
              </TableRow>
            );
          })}

          {/* Summary row */}
          {summary && (
            <TableRow className="border-b border-zinc-300 dark:border-zinc-600 font-bold">
              <TableCell className="py-2 px-2" style={{ width: '40px' }} />
              {visibleColumns.has('unitName') && (
                <TableCell className="font-bold text-zinc-100 py-2 px-2">
                  Avg
                </TableCell>
              )}
              {showComparabilityReason && <TableCell className="py-2 px-2" />}
              {visibleColumns.has('line') && <TableCell className="font-bold text-zinc-100 py-2 px-2" />}
              {visibleColumns.has('bedrooms') && <TableCell align="right" className="font-bold text-zinc-100 py-2 px-2" />}
              {visibleColumns.has('sqft') && <TableCell align="right" className="font-bold text-zinc-100 py-2 px-2" />}
              {visibleColumns.has('rent') && (
                <TableCell align="right" className="font-bold text-zinc-100 py-2 px-2">
                  ${getLongNumberStr(Math.round(summary.avgRent))}
                </TableCell>
              )}
              {visibleColumns.has('ppsf') && (
                <TableCell align="right" className="font-bold text-zinc-100 py-2 px-2">
                  ${summary.avgPPSF.toFixed(2)}
                </TableCell>
              )}
              {visibleColumns.has('concessions') && <TableCell align="right" className="font-bold text-zinc-100 py-2 px-2" />}
              {visibleColumns.has('effectiveRent') && (
                <TableCell align="right" className="font-bold text-zinc-100 py-2 px-2">
                  ${getLongNumberStr(Math.round(summary.avgEffectiveRent))}
                </TableCell>
              )}
              {visibleColumns.has('effectivePPSF') && (
                <TableCell align="right" className="font-bold text-zinc-100 py-2 px-2">
                  ${summary.avgEffectivePPSF.toFixed(2)}
                </TableCell>
              )}
              {visibleColumns.has('leaseLength') && <TableCell align="right" className="font-bold text-zinc-100 py-2 px-2" />}
              {visibleColumns.has('leaseStart') && <TableCell align="right" className="font-bold text-zinc-100 py-2 px-2" />}
              {visibleColumns.has('leaseEnd') && <TableCell align="right" className="font-bold text-zinc-100 py-2 px-2" />}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
